/**
 * useMe — the authenticated-user context for the operator console. Fetches
 * /api/auth/me once, exposes the user's role, action-level permissions, and
 * accessible property ids, plus helpers the sidebar / route guards / property
 * selectors / action buttons consult. Backend remains the source of truth; this
 * only drives what the UI shows.
 */
import { createContext, useContext, useEffect, useState, type ReactElement, type ReactNode } from "react";

export interface MeUser {
  readonly userId: string;
  readonly tenantId: string;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly role: string | null;
  readonly permissions: readonly string[];
  readonly propertyAccessAll: boolean;
  readonly accessiblePropertyIds: readonly string[];
  readonly mfaEnrolled: boolean;
  readonly organisationName: string;
  /**
   * Whether the user must set a new password before using the app (first-login
   * temporary-password flow). The server is the real gate (it 403s /api/* and
   * redirects /app/*); this drives a clean client-side redirect to
   * /change-password as a backstop.
   */
  readonly mustChangePassword?: boolean;
  readonly demo?: boolean;
}

export interface MeState {
  readonly loading: boolean;
  readonly user: MeUser | null;
  readonly can: (permission: string) => boolean;
  readonly canAccessProperty: (propertyId: string | null | undefined) => boolean;
  readonly reload: () => void;
}

const Ctx = createContext<MeState | null>(null);

/**
 * One-shot navigation guard shared across the console. Once a redirect to
 * /login has been initiated (by logout OR by a detected unauthenticated
 * session), no other code path fires a second navigation. A full-document
 * navigation unloads the page anyway, but React effects can run again in the
 * async window before unload; this flag makes "go to /login" idempotent and
 * turns UNAUTHENTICATED into a STABLE state instead of a reload loop.
 */
let navigatingToLogin = false;

/** Test-only: reset the one-shot login-navigation guard between tests. */
export function __resetLoginNavigationGuardForTests(): void {
  navigatingToLogin = false;
}

/** Navigate to /login exactly once. Safe to call from multiple places. */
export function goToLoginOnce(): void {
  if (typeof window === "undefined") return;
  if (navigatingToLogin) return;
  // Already on the login page → nothing to do (prevents a /login → /login loop).
  if (window.location.pathname === "/login") return;
  navigatingToLogin = true;
  window.location.replace("/login");
}

export function MeProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<MeUser | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetch("/api/auth/me", { method: "POST", headers: { "content-type": "application/json" }, credentials: "include", body: "{}" })
      .then(async (r) => ({ status: r.status, body: r.status === 200 ? await r.json() : null }))
      .then(({ status, body }) => {
        if (!live) return;
        setUser(body?.user ?? null);
        setLoading(false);
        // Session validation on every mount/refresh: if the server reports the
        // request is unauthenticated (expired / revoked / no session) while we
        // are inside the authenticated app, bounce to /login. The server-side
        // route guard is the real control; this clears stale client state and
        // prevents a cached /app view from appearing usable after logout or
        // expiry (back-button, manual URL, refresh, idle timeout).
        // UNAUTHENTICATED is a STABLE state: when the server reports no valid
        // session (expired / revoked / logged out) while inside the app, go to
        // /login AT MOST ONCE via the shared one-shot guard. This must never
        // loop — goToLoginOnce() is idempotent and a no-op when already on
        // /login. The server-side route guard remains the real control.
        if (status === 401 && typeof window !== "undefined") {
          const path = window.location.pathname;
          if (path === "/app" || path.startsWith("/app/")) {
            goToLoginOnce();
          }
        }
      })
      .catch(() => { if (live) { setUser(null); setLoading(false); } });
    return () => { live = false; };
  }, [tick]);

  const permSet = new Set(user?.permissions ?? []);
  const value: MeState = {
    loading,
    user,
    can: (permission) => permSet.has(permission),
    canAccessProperty: (propertyId) => {
      if (!user) return false;
      if (user.propertyAccessAll) return true;
      if (!propertyId) return true;
      return user.accessiblePropertyIds.includes(String(propertyId));
    },
    reload: () => setTick((t) => t + 1),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMe(): MeState {
  const ctx = useContext(Ctx);
  if (ctx === null) {
    // Defensive default when used outside a provider (SSR/standalone).
    return { loading: false, user: null, can: () => false, canAccessProperty: () => false, reload: () => undefined };
  }
  return ctx;
}

/** Access Denied panel shown when a route/module is not permitted. */
export function AccessDenied(): ReactElement {
  return (
    <div className="access-denied">
      <h1 className="access-denied__title">Access denied</h1>
      <p className="access-denied__body">You don't have permission to access this area.</p>
      <p className="access-denied__hint">Contact your organisation administrator if you need access.</p>
      <a className="access-denied__cta" href="/app/dashboard">Back to dashboard</a>
    </div>
  );
}
